import { Badge } from '../../../core/badge';
import { Separator } from '../../../core/separator';
import type { ExerciseSubmissionRecord } from '../../types/exercise';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { TeacherCommentEditor } from './teacher-comment-editor';
import { formatDate, type TeacherComment } from './exercise-submission-helpers';
import { SubmissionContentRenderer } from './submission-content-renderer';
import { EvaluationRenderer } from './evaluation-renderer';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface ExerciseSubmissionHistoryProps {
  content: StudentExerciseContent;
  submissions: ExerciseSubmissionRecord[];
  isTeacherView?: boolean;
}

export function ExerciseSubmissionHistory({ content, submissions, isTeacherView = false }: ExerciseSubmissionHistoryProps) {
  if (!submissions.length) {
    return null;
  }

  return (
    <div className="space-y-3 rounded-lg border border-rose-100 bg-rose-50/50 p-4 text-sm text-gray-800">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.2em] text-rose-500">Historico de envios</p>
        <span className="text-xs text-gray-500">{submissions.length} tentativa(s)</span>
      </div>
      <div className="space-y-3">
        {submissions.map((submission, index) => (
          <SubmissionHistoryCard
            key={submission.submissionId}
            submission={submission}
            content={content}
            isTeacherView={isTeacherView}
            isLatestSubmission={index === 0}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card for a single submission attempt
// ---------------------------------------------------------------------------

function SubmissionHistoryCard({
  submission,
  content,
  isTeacherView = false,
  isLatestSubmission = false,
}: {
  submission: ExerciseSubmissionRecord;
  content: StudentExerciseContent;
  isTeacherView?: boolean;
  isLatestSubmission?: boolean;
}) {
  const scoreLabel =
    typeof submission.score === 'number' && typeof submission.maxScore === 'number'
      ? `${submission.score}/${submission.maxScore}`
      : null;

  const teacherComment = (submission.evaluation as Record<string, unknown> | undefined)?.teacher as TeacherComment | undefined;

  return (
    <div className="rounded-md border border-white/60 bg-white/80 p-3 shadow-sm">
      {/* Header: attempt badge, date, score, status */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">
            Tentativa #{submission.attemptNumber}
          </Badge>
          <span>{formatDate(submission.submittedAt)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {scoreLabel && (
            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
              Nota {scoreLabel}
            </Badge>
          )}
          {submission.status && (
            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
              {submission.status}
            </Badge>
          )}
          {submission.needsManualGrading && (
            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
              Revisao manual
            </Badge>
          )}
        </div>
      </div>

      <Separator className="my-3" />

      {/* Submission content */}
      <div className="space-y-2">
        <SubmissionContentRenderer
          submission={submission.submission}
          content={content}
          evaluation={submission.evaluation ?? null}
        />
      </div>

      {/* Passing score */}
      {submission.passingScore !== null && (
        <p className="mt-2 text-xs text-gray-500">
          Pontuacao de aprovacao: {submission.passingScore}
          {submission.maxScore ? ` de ${submission.maxScore}` : ''}
        </p>
      )}

      {/* Auto evaluation */}
      {submission.evaluation && (
        <>
          <Separator className="my-3" />
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">Avaliacao automatica</p>
            <EvaluationRenderer evaluation={submission.evaluation} content={content} />
          </div>
        </>
      )}

      {/* Teacher comment - only on latest submission */}
      {isTeacherView && isLatestSubmission && (
        <>
          <Separator className="my-3" />
          <TeacherCommentEditor
            submissionId={submission.submissionId}
            existingComment={teacherComment}
          />
        </>
      )}
    </div>
  );
}
